
import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { Store } from '@ngrx/store';
import { DateOption, DateRange, getDateRangeFromDateOption, isSelectedOrganizationFilters, isSelectedProgramFilters, SelectedFilters } from '../../models/filters.model';
import { Organization, Site } from '../../models/organization.model';
import { ProgramOrganizationType } from '../../models/program-organization.model';
import { Product, Program } from '../../models/program.model';
import { loadProductsForProgram, loadSitesForOrganization, setFilters } from '../actions/pd.actions';

// Local interface
export interface FiltersLocalState {
    program: Program | undefined;
    products: Product[];
    organization: Organization | undefined;
    sites: Site[];
    isFirstLoad: boolean;
    orgDateRange: DateRange;
    orgDateOption: DateOption;
    progDateRange: DateRange;
    progDateOption: DateOption;
}

const defaultDateRange = getDateRangeFromDateOption(DateOption.last3Months);

const initialState: FiltersLocalState = {
    program: undefined,
    organization: undefined,
    products: [],
    sites: [],
    isFirstLoad: true,
    orgDateRange: defaultDateRange,
    orgDateOption: DateOption.last3Months,
    progDateRange: defaultDateRange,
    progDateOption: DateOption.last3Months,
};

export const FiltersStore = signalStore(
    withState(initialState),
    withComputed((state) => ({
        showProductFilter: computed(() => !!state.program()),
    })),
    withMethods((store, globalStore = inject(Store)) => ({

        // Global to Local Sync
        syncFromGlobal(
            applied: SelectedFilters | undefined,
            viewMode: ProgramOrganizationType,
            programs?: Program[],
            _organizations?: Organization[],
        ) {
            if (applied) {
                if (isSelectedOrganizationFilters(applied)) {
                    patchState(store, {
                        organization: applied.organization,
                        sites: applied.sites || [],
                        orgDateRange: applied.dateRange,
                        orgDateOption: applied.dateOption
                    });
                }
                else if (isSelectedProgramFilters(applied)) {
                    patchState(store, {
                        program: applied.program,
                        products: applied.products || [],
                        progDateRange: applied.dateRange,
                        progDateOption: applied.dateOption
                    });
                }
                return;
            }

            if (viewMode === ProgramOrganizationType.Program && programs && programs.length > 0) {
                patchState(store, { program: programs[0] });
                globalStore.dispatch(loadProductsForProgram({ programId: programs[0].id }));
            }
        },

        updateProgram(program: Program) {
            patchState(store, {
                program,
                products: []
            });
            globalStore.dispatch(loadProductsForProgram({ programId: program.id }));
        },
        toggleOrganization(organization: Organization) {
            const isCurrentlySelected = store.organization()?.id === organization.id;
            patchState(store, {
                organization: isCurrentlySelected ? undefined : organization,
                sites: []
            });
            // Load sites for the selected organization
            if (!isCurrentlySelected) {
                globalStore.dispatch(loadSitesForOrganization({ organizationId: organization.id }));
            }
        },

        toggleProduct(product: Product) {
            const current = store.products();
            const isSelected = current.some(p => p.id === product.id);
            patchState(store, {
                products: isSelected
                    ? current.filter(p => p.id !== product.id)
                    : [...current, product]
            });
        },
        //This is used to set the initial load of filters for products
        toggleProducts(products: Product[]) {
            const current = store.products();
            patchState(store, {
                isFirstLoad: false,
                products: [...current, ...products.filter(p => !current.some(cp => cp.id === p.id))]
            });
        },
        toggleSite(site: Site) {
            const current = store.sites();
            const isSelected = current.some(s => s.id === site.id);
            patchState(store, {
                sites: isSelected
                    ? current.filter(s => s.id !== site.id)
                    : [...current, site]
            });
        },
        //This is used to set the initial load of filters for sites
        toggleSites(sites: Site[]) {
            const current = store.sites();
            patchState(store, {
                isFirstLoad: false,
                sites: [...current, ...sites.filter(s => !current.some(cs => cs.id === s.id))]
            });
        },
        setFirstLoad(isFirstLoad: boolean) {
            patchState(store, { isFirstLoad });
        },
        updateDateRange(view: ProgramOrganizationType, option: DateOption, start?: Date, end?: Date) {
            const dateRange = getDateRangeFromDateOption(option, start, end);
            if (view === ProgramOrganizationType.Organization) {
                patchState(store, { orgDateOption: option, orgDateRange: dateRange });
            } else {
                patchState(store, { progDateOption: option, progDateRange: dateRange });
            }
        },
        reset() {
            patchState(store, initialState);
        },
        apply(viewMode: ProgramOrganizationType) {
            if (viewMode === ProgramOrganizationType.Organization) {
                const organization = store.organization();
                if (!organization) return;
                globalStore.dispatch(setFilters({
                    filters: {
                        organization,
                        sites: store.sites(),
                        dateRange: store.orgDateRange(),
                        dateOption: store.orgDateOption()
                    }
                }));
                return;
            }

            const program = store.program();
            if (!program) return;
            globalStore.dispatch(setFilters({
                filters: {
                    program,
                    products: store.products(),
                    dateRange: store.progDateRange(),
                    dateOption: store.progDateOption()
                }
            }));
        }
    })),
);




